import { QueryList } from '@angular/core';

import {
  MonoTypeOperatorFunction,
  Observable,
  SchedulerLike,
  asyncScheduler,
  concat,
  connect,
  debounceTime,
  map,
  startWith,
  take
} from 'rxjs';

export function debounceTimeAfter<T>(
  amount: number,
  dueTime: number,
  scheduler: SchedulerLike = asyncScheduler
): MonoTypeOperatorFunction<T> {
  return connect(value => concat(value.pipe(take(amount)), value.pipe(debounceTime(dueTime, scheduler))));
}

export function debounceTimeAfterFirst<T>(
  dueTime: number,
  scheduler: SchedulerLike = asyncScheduler
): MonoTypeOperatorFunction<T> {
  return debounceTimeAfter<T>(1, dueTime, scheduler);
}

/**
 * Creates an observable of a QueryList (from @ContentChildren or @ViewChildren) starting with the first value of the QueryList
 * @param query The Query List
 * @example
 * With ContentChildren decorator
 * ```ts
 * import { queryListToArrayObservable } from '@hdms/shared/util-core';
 *
 * class MyComponent implements AfterContentInit {
 *   @ContentChildren(MyChildDirective)
 *   private childDirectiveQueryList: QueryList<MyChildDirective>
 *
 *   ngAfterContentInit: void {
 *      queryListToArrayObservable(this.childDirectiveQueryList)
 *        .subscribe((items: MyChildDirective[]) => {
 *          // do something
 *        });
 *   }
 * }
 * ```
 */
export function queryListToArrayObservable<T>(query: QueryList<T>): Observable<T[]> {
  return query.changes.pipe(
    startWith(query),
    map(result => result.toArray())
  );
}
